import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, CheckCircle, Users, Cpu, FolderKanban, Mail, Phone, UserCircle } from "lucide-react";
import Header from "../components/layout/Header";
import { StatCard, Badge, SearchInput, Button, Table } from "../components/ui/index";
import { clientsAPI, cascadeAPI } from "../api/services";
import { formatDateShort } from "../utils/dateUtils";
import { useAuth } from "../context/AuthContext";
import AddClientModal from "../components/modals/AddClientModal";
import EditClientModal from "../components/modals/EditClientModal";
import ActionMenu from "../components/ui/ActionMenu";

export default function Clients() {
  const navigate = useNavigate();
  const { user, adminMode } = useAuth();
  const { t } = useTranslation();
  const isRevendeur = user?.role === 'revendeur';
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [clients, setClients] = useState([]);
  const [revendeurs, setRevendeurs] = useState([]);
  const [filterRevendeur, setFilterRevendeur] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const params = { search };
      if (filterRevendeur) params.revendeur = filterRevendeur;
      const res = await clientsAPI.getAll(params);
      setClients(res.data.data);
    } catch (err) {
      console.error("Erreur chargement clients:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cascadeAPI.revendeurs().then(res => setRevendeurs(res.data.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [search, filterRevendeur]); // eslint-disable-line

  const handleAdd = async (newClient) => {
    try {
      await clientsAPI.create(newClient);
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error("Erreur création:", err);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await clientsAPI.remove(confirmDelete._id);
      setConfirmDelete(null);
      fetchData();
    } catch (err) {
      console.error("Erreur suppression:", err);
    }
  };

  const handleEdit = async (updatedData) => {
    try {
      await clientsAPI.update(editItem._id, updatedData);
      setEditItem(null);
      fetchData();
    } catch (err) {
      console.error("Erreur modification:", err);
    }
  };

  const totalAppareils = clients.reduce((s, c) => s + (c.appareils || 0), 0);

  return (
    <>
      <Header title={t('clients.title')} subtitle={`${clients.length} ${t('clients.clientsCount')}`} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <StatCard icon={Users} iconBg="bg-green-100 text-green-600" label="Total clients" value={clients.length} />
        <StatCard icon={Cpu} iconBg="bg-blue-100 text-blue-600" label={t('clients.deployedDevices')} value={totalAppareils} onClick={() => navigate('/noeuds')} />
        <StatCard icon={FolderKanban} iconBg="bg-orange-100 text-orange-600" label={t('clients.totalProjects')} value="—" onClick={() => navigate('/projets')} />
        <Button onClick={() => setShowModal(true)} className="h-full justify-center">
          <Plus size={18} /> {t('clients.addClient')}
        </Button>
      </div>

      {adminMode === 'platform' && (
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
          <select value={filterRevendeur} onChange={e => setFilterRevendeur(e.target.value)}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">{t('clients.allResellers')} ({revendeurs.length})</option>
            {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
          </select>
        </div>
      )}

      <div className="mb-4">
        <SearchInput value={search} onChange={setSearch} />
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>
      ) : (
        <Table headers={[t('clients.colClient'), t('clients.colContact'), t('clients.colReseller'), t('clients.colType'), t('clients.colDevices'), t('clients.colStatus'), t('clients.colActions')]}>
          {clients.map((c) => (
            <tr key={c._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                    <UserCircle size={20} className="text-slate-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{c.nom}</p>
                    <p className="text-xs text-slate-400">{t('common.since')} {formatDateShort(c.createdAt)}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-slate-600"><Mail size={12} className="text-slate-400" />{c.email}</div>
                  {c.telephones?.[0] && <div className="flex items-center gap-2 text-xs text-slate-600"><Phone size={12} className="text-slate-400" />{c.telephones[0]}</div>}
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-slate-700">{c.revendeur?.nom || t('clients.directAdmin')}</td>
              <td className="px-6 py-4"><Badge label={c.type} /></td>
              <td className="px-6 py-4">
                <p className="text-sm font-semibold text-slate-800">{c.appareils || 0} {t('clients.total')}</p>
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle size={11} />{c.appareilsActifs || 0} {t('clients.active')}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${c.statut === 'Actif' ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className={`text-sm font-medium ${c.statut === 'Actif' ? 'text-green-600' : 'text-red-600'}`}>{c.statut}</span>
                </div>
              </td>
              <td className="px-6 py-4">
                <ActionMenu
                  rowId={c._id}
                  openMenu={openMenu}
                  setOpenMenu={setOpenMenu}
                  onEdit={() => setEditItem(c)}
                  onDelete={() => setConfirmDelete(c)}
                />
              </td>
            </tr>
          ))}
        </Table>
      )}

      <AddClientModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onAdd={handleAdd}
      />

      <EditClientModal
        isOpen={!!editItem}
        onClose={() => setEditItem(null)}
        onEdit={handleEdit}
        item={editItem}
      />

      {/* Modal confirmation suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-2xl p-6 w-96 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-800 text-lg mb-2">{t('clients.deleteClient')}</h3>
            <p className="text-sm text-slate-500 mb-6">
              {t('clients.confirmDelete')} <strong>{confirmDelete.nom}</strong> ? {t('clients.irreversible')}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">
                {t('clients.cancel')}
              </button>
              <button onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700">
                {t('clients.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
